import React, { useState } from 'react';
import { Button, Form, Spinner, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { generateDteForReceipt } from '../../../../../../../../../../../../crud/payments_api/dte';
import './CreateDte.scss';

const DTE_TYPES: { [key: number]: string } = {
  33: 'Factura Electrónica',
  34: 'Factura No Afecta o Exenta',
  39: 'Boleta Electrónica',
  41: 'Boleta Exenta',
  46: 'Factura de Compra',
  52: 'Guía de Despacho',
  56: 'Nota de Débito',
  61: 'Nota de Crédito'
};

// DTE types that are currently enabled
const ENABLED_DTE_TYPES = [33, 39];

// DTE types that require full receptor info (factura)
const DTE_REQUIRES_RECEPTOR_INFO = [33, 34, 46, 52, 56, 61];

export interface CreatedDteData {
  dteId: number;
  folio: number;
  tipoDte: number;
  localId: number;
}

interface CreateDteProps {
  show: boolean;
  receiptId: number;
  localId: number;
  onSuccess: (createdDte?: CreatedDteData) => void;
  onCancel: () => void;
}

const CreateDte: React.FC<CreateDteProps> = ({ show, receiptId, localId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    dte_type: 39,
    rut: '',
    razon_social: '',
    giro: '',
    direccion: '',
    comuna: '',
    contacto: '',
    forma_pago: 1,
    indicador_servicio: 3
  });
  const [creating, setCreating] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<{ error: string; details: string } | null>(null);

  // Check if the selected DTE type requires receptor info
  const requiresReceptorInfo = DTE_REQUIRES_RECEPTOR_INFO.includes(formData.dte_type);

  // Debug log
  console.log('CreateDte render - show:', show, 'receiptId:', receiptId, 'localId:', localId);

  const handleCreateDte = async () => {
    // For DTE types that require receptor info, validate fields
    if (requiresReceptorInfo) {
      // Validate RUT format
      const rutRegex = /^(\d{7,8})-(\d|k|K)$/;
      if (!rutRegex.test(formData.rut)) {
        toast.error('El RUT debe tener formato XXXXXXX-X o XXXXXXXX-X');
        return;
      }

      // Validate required fields
      if (!formData.razon_social) {
        toast.error('Razón social es obligatoria');
        return;
      }
    }

    setCreating(true);
    setErrorDetails(null); // Clear previous errors
    
    try {
      // Build payload based on DTE type
      let payload: any;
      
      if (formData.dte_type === 39) {
        // Boleta Electrónica - simplified payload
        payload = {
          dte_type: 39,
          indicador_servicio: formData.indicador_servicio
        };
      } else {
        // Factura and other types - full payload
        payload = {
          dte_type: formData.dte_type,
          rut: formData.rut,
          razon_social: formData.razon_social,
          giro: formData.giro,
          direccion: formData.direccion,
          comuna: formData.comuna,
          contacto: formData.contacto,
          forma_pago: formData.forma_pago
        };
      }
      
      const response = await generateDteForReceipt(localId, receiptId, payload);
      
      toast.success('DTE creado exitosamente');
      setErrorDetails(null);
      onSuccess({
        dteId: response.data.dte_record_id,
        folio: response.data.folio,
        tipoDte: formData.dte_type,
        localId: localId
      });
    } catch (error: any) {
      console.error('Error creating DTE:', error);
      
      // Extract error details from response
      const errorData = error.response?.data;
      let errorMessage = 'No fue posible generar el DTE';
      let errorDetailsText = '';
      
      if (errorData) {
        errorMessage = errorData.error || errorMessage;
        
        // Try to parse details if they exist
        if (errorData.details) {
          if (typeof errorData.details === 'string') {
            errorDetailsText = errorData.details;
          } else if (errorData.details.message) {
            errorDetailsText = errorData.details.message;
            
            // Try to parse nested details if they exist
            if (errorData.details.details) {
              try {
                const nestedDetails = JSON.parse(errorData.details.details);
                if (nestedDetails.mensaje) {
                  errorDetailsText = nestedDetails.mensaje;
                }
              } catch (e) {
                // If parsing fails, use the raw details
                errorDetailsText = errorData.details.details;
              }
            }
          }
        }
      }
      
      // Set error for banner display
      setErrorDetails({
        error: errorMessage,
        details: errorDetailsText
      });
      
      // Also show a toast for immediate feedback
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onCancel} centered className="create-dte-modal">
      <Modal.Header className="d-flex align-items-center justify-content-between">
        <div className="form-modal-title-container">
          <h4 className="form-modal-title">Crear nuevo DTE</h4>
        </div>
        <button 
          type="button" 
          className="modal-close-button"
          onClick={onCancel}
          aria-label="Close"
        >
          ×
        </button>
      </Modal.Header>
      
      <Modal.Body>
        <p className="text-muted mb-4">
          Complete la información del receptor para generar el documento tributario electrónico
        </p>
        
        {errorDetails && (
          <div className="error-banner">
            <div className="error-banner-header">
              <strong>⚠️ Error al generar DTE</strong>
            </div>
            <div className="error-banner-content">
              <p className="error-message">{errorDetails.error}</p>
              {errorDetails.details && (
                <div className="error-details">
                  <small>{errorDetails.details}</small>
                </div>
              )}
            </div>
            <button 
              className="error-banner-close"
              onClick={() => setErrorDetails(null)}
              type="button"
            >
              ×
            </button>
          </div>
        )}
        
        <Form>
        {/* Tipo de DTE */}
        <Form.Group className="mb-3">
          <Form.Label>Tipo de documento</Form.Label>
          <Form.Select
            value={formData.dte_type}
            onChange={(e) => setFormData({ ...formData, dte_type: parseInt(e.target.value) })}
            disabled={creating}
          >
            {Object.entries(DTE_TYPES).map(([tipo, nombre]) => (
              <option 
                key={tipo} 
                value={tipo}
                disabled={!ENABLED_DTE_TYPES.includes(parseInt(tipo))}
              >
                {`${tipo} - ${nombre}${!ENABLED_DTE_TYPES.includes(parseInt(tipo)) ? ' (Próximamente)' : ''}`}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            {formData.dte_type === 39 
              ? 'Boleta electrónica - No requiere datos del receptor'
              : 'Factura electrónica - Requiere datos del receptor'
            }
          </Form.Text>
        </Form.Group>

        {/* Indicador de Servicio - Only for Boleta (39) */}
        {formData.dte_type === 39 && (
          <Form.Group className="mb-3">
            <Form.Label>Indicador de Servicio</Form.Label>
            <Form.Select
              value={formData.indicador_servicio}
              onChange={(e) => setFormData({ ...formData, indicador_servicio: parseInt(e.target.value) })}
              disabled={creating}
            >
              <option value={1}>Servicio periódico domiciliario</option>
              <option value={2}>Servicio periódico no domiciliario</option>
              <option value={3}>Venta y servicios</option>
              <option value={4}>Espectáculo</option>
            </Form.Select>
          </Form.Group>
        )}

        {/* Campos adicionales solo para Factura (33) y otros que requieren receptor */}
        {requiresReceptorInfo && (
          <>
            {/* RUT Receptor */}
            <Form.Group className="mb-3">
              <Form.Label>RUT Receptor *</Form.Label>
              <Form.Control
                type="text"
                placeholder="12345678-9"
                value={formData.rut}
                onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                disabled={creating}
              />
              <Form.Text className="text-muted">
                Formato: XXXXXXXX-X
              </Form.Text>
            </Form.Group>

            {/* Razón Social */}
            <Form.Group className="mb-3">
              <Form.Label>Razón Social *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Empresa S.A."
                value={formData.razon_social}
                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                disabled={creating}
              />
            </Form.Group>

            {/* Giro */}
            <Form.Group className="mb-3">
              <Form.Label>Giro</Form.Label>
              <Form.Control
                type="text"
                placeholder="Comercio al por menor"
                value={formData.giro}
                onChange={(e) => setFormData({ ...formData, giro: e.target.value })}
                disabled={creating}
              />
            </Form.Group>

            {/* Dirección */}
            <Form.Group className="mb-3">
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                type="text"
                placeholder="Calle 123"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                disabled={creating}
              />
            </Form.Group>

            {/* Comuna */}
            <Form.Group className="mb-3">
              <Form.Label>Comuna</Form.Label>
              <Form.Control
                type="text"
                placeholder="Santiago"
                value={formData.comuna}
                onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                disabled={creating}
              />
            </Form.Group>

            {/* Contacto */}
            <Form.Group className="mb-3">
              <Form.Label>Contacto (Teléfono)</Form.Label>
              <Form.Control
                type="text"
                placeholder="+56912345678"
                value={formData.contacto}
                onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                disabled={creating}
              />
            </Form.Group>

            {/* Forma de Pago */}
            <Form.Group className="mb-3">
              <Form.Label>Forma de Pago</Form.Label>
              <Form.Select
                value={formData.forma_pago}
                onChange={(e) => setFormData({ ...formData, forma_pago: parseInt(e.target.value) })}
                disabled={creating}
              >
                <option value={1}>Contado</option>
                <option value={2}>Crédito</option>
                <option value={3}>Sin costo</option>
              </Form.Select>
            </Form.Group>
          </>
        )}
      </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button 
            className="form-button-cancel"
            onClick={onCancel}
            disabled={creating}
          >
            Cancelar
          </Button>
          <Button 
            className="form-button-create"
            onClick={handleCreateDte}
            disabled={creating}
          >
            {creating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              'Crear DTE'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateDte;
